"use client"

import { useCallback } from "react"
import { Facebook, LinkIcon, Twitter } from "lucide-react"
import { useTranslations } from "next-intl"
import { cn } from "@/shared/lib/utils"

interface ArticleShareButtonsProps {
  title: string
  variant?: "default" | "compact"
}

export function ArticleShareButtons({ title, variant = "default" }: ArticleShareButtonsProps) {
  const t = useTranslations("blog.article")
  const handleCopyLink = useCallback(() => {
    if (!navigator.clipboard) return
    void navigator.clipboard.writeText(window.location.href).catch(() => undefined)
  }, [])

  const handleShareTwitter = useCallback(() => {
    const url = encodeURIComponent(window.location.href)
    const text = encodeURIComponent(title)
    window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, "_blank")
  }, [title])

  const handleShareFacebook = useCallback(() => {
    const url = encodeURIComponent(window.location.href)
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, "_blank")
  }, [])

  const isCompact = variant === "compact"

  return (
    <div
      className={cn(
        "flex items-center gap-2",
        isCompact ? "" : "mt-8 border-t border-gray-100 pt-6"
      )}
    >
      <span
        className={cn(
          "font-medium",
          isCompact ? "mr-1 text-[13px] text-[#4b5563]" : "text-[13px] text-[#9ca3af]"
        )}
      >
        {isCompact ? t("shareCompact") : t("shareFull")}
      </span>
      <button
        type="button"
        onClick={handleShareTwitter}
        className={cn(
          "transition-colors hover:border-[#1d7df2]/30 hover:text-[#1d7df2]",
          isCompact
            ? "rounded-md bg-[#f3f4f6] p-2 text-[#111827] hover:bg-[#eaf2ff]"
            : "rounded-lg border border-gray-200 p-2 text-[#9ca3af]"
        )}
        aria-label={t("shareTwitter")}
      >
        <Twitter className="size-4" />
      </button>
      <button
        type="button"
        onClick={handleShareFacebook}
        className={cn(
          "transition-colors hover:border-[#1d7df2]/30 hover:text-[#1d7df2]",
          isCompact
            ? "rounded-md bg-[#f3f4f6] p-2 text-[#111827] hover:bg-[#eaf2ff]"
            : "rounded-lg border border-gray-200 p-2 text-[#9ca3af]"
        )}
        aria-label={t("shareFacebook")}
      >
        <Facebook className="size-4" />
      </button>
      <button
        type="button"
        onClick={handleCopyLink}
        className={cn(
          "transition-colors hover:border-[#1d7df2]/30 hover:text-[#1d7df2]",
          isCompact
            ? "rounded-md bg-[#f3f4f6] p-2 text-[#111827] hover:bg-[#eaf2ff]"
            : "rounded-lg border border-gray-200 p-2 text-[#9ca3af]"
        )}
        aria-label={t("copyLink")}
      >
        <LinkIcon className="size-4" />
      </button>
    </div>
  )
}
